import { useState, useEffect } from 'react';
import { rollupApi, type TrustRollup, type CampusRollup } from '../lib/analyticsApi';

const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const short = (p: number) => {
  const r = p / 100;
  if (r >= 10000000) return '₹' + (r / 10000000).toFixed(2) + ' Cr';
  if (r >= 100000) return '₹' + (r / 100000).toFixed(1) + ' L';
  if (r >= 1000) return '₹' + Math.round(r / 1000) + 'k';
  return '₹' + Math.round(r);
};
const rateColour = (pct: number) => pct < 60 ? '#c1272d' : pct < 85 ? '#b26a00' : '#1a7f37';

export function TrustRollupPage() {
  const [data, setData] = useState<TrustRollup | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    rollupApi.trust().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (!data) return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the rollup.</div>;

  const t = data.totals;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Trust</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>All campuses</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Academic year {data.academicYear} · {t.campuses} campus{t.campuses === 1 ? '' : 'es'} ·{' '}
          {t.institutions} institution{t.institutions === 1 ? '' : 's'}
        </p>
      </div>

      {/* ---- things to look at first ---- */}
      {data.flags.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <strong style={{ fontSize: 14 }}>Needs attention</strong>
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.flags.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: 999, marginTop: 6, flexShrink: 0,
                  background: f.severity === 'high' ? '#c1272d' : f.severity === 'medium' ? '#b26a00' : 'var(--ink-3)',
                }} />
                <span><strong>{f.campusName}</strong> — {f.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- trust totals ---- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Stat label="Students" value={t.activeStudents.toLocaleString('en-IN')} sub={`${t.newAdmissionsThisYear} admitted this year`} />
        <Stat label="Billed" value={short(t.billedPaise)} sub={rupees(t.billedPaise)} />
        <Stat label="Collected" value={short(t.collectedPaise)} sub={`${t.collectionRatePct}% of billed`} tone="good" />
        <Stat label="Outstanding" value={short(t.outstandingPaise)} sub={`${t.studentsWithDues} students owe`} />
        <Stat label="Overdue" value={short(t.overduePaise)} sub="past due date" tone={t.overduePaise > 0 ? 'bad' : undefined} />
        <Stat label="Attendance" value={`${t.attendanceRatePct}%`} sub="weighted across all campuses" />
      </div>

      {data.caveat && (
        <div className="card" style={{ padding: 12, marginBottom: 16, fontSize: 12, color: 'var(--ink-2)', borderLeft: '3px solid var(--brand, #8a6a2e)' }}>
          {data.caveat}
        </div>
      )}

      {/* ---- campus by campus ---- */}
      <h2 className="h-display" style={{ fontSize: 18, marginBottom: 4 }}>Campuses</h2>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>Weakest collection first.</p>

      {data.campuses.length === 0
        ? <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>No campuses set up yet.</div>
        : data.campuses.map((c) => (
          <CampusCard key={c.campusId} c={c} open={open === c.campusId}
            onToggle={() => setOpen(open === c.campusId ? null : c.campusId)} />
        ))}
    </div>
  );
}

function CampusCard({ c, open, onToggle }: { c: CampusRollup; open: boolean; onToggle: () => void }) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{c.campusName}</span>
          {c.city && <span style={{ color: 'var(--ink-3)', fontSize: 13 }}> · {c.city}</span>}
          {!c.isActive && <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Inactive</span>}
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            {c.institutions} institution{c.institutions === 1 ? '' : 's'} · {c.activeStudents.toLocaleString('en-IN')} students
            {c.newAdmissionsThisYear > 0 && ` · ${c.newAdmissionsThisYear} new`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Collected</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: rateColour(Number(c.collectionRatePct)) }}>
            {c.collectionRatePct}%
          </div>
        </div>
        <button className="btn" onClick={onToggle}>{open ? 'Hide' : 'Institutions'}</button>
      </div>

      <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 12, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
        <span>Billed <strong>{short(c.billedPaise)}</strong></span>
        <span>Collected <strong>{short(c.collectedPaise)}</strong></span>
        <span>Outstanding <strong style={{ color: c.outstandingPaise > 0 ? '#c1272d' : 'inherit' }}>{short(c.outstandingPaise)}</strong></span>
        {c.overduePaise > 0 && <span>Overdue <strong style={{ color: '#c1272d' }}>{short(c.overduePaise)}</strong></span>}
        {c.attendanceRatePct > 0 && <span>Attendance <strong>{c.attendanceRatePct}%</strong></span>}
      </div>

      <div style={{ height: 6, background: 'var(--surface-2,#eee)', borderRadius: 999, overflow: 'hidden', marginTop: 10 }}>
        <div style={{ width: `${Math.min(100, Number(c.collectionRatePct))}%`, height: '100%', background: rateColour(Number(c.collectionRatePct)) }} />
      </div>

      {open && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-3)', fontSize: 11 }}>
                <th style={{ padding: '4px 8px' }}>INSTITUTION</th><th>TYPE</th><th>STUDENTS</th>
                <th>BILLED</th><th>OUTSTANDING</th><th>COLLECTED</th><th>ATTENDANCE</th>
              </tr>
            </thead>
            <tbody>
              {c.institutionBreakdown.map((i) => (
                <tr key={i.institutionId} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 500 }}>{i.institutionName}</td>
                  <td style={{ color: 'var(--ink-3)' }}>{i.institutionType}</td>
                  <td>{i.activeStudents}</td>
                  <td>{short(i.billedPaise)}</td>
                  <td style={{ color: i.outstandingPaise > 0 ? '#c1272d' : 'inherit' }}>{short(i.outstandingPaise)}</td>
                  <td style={{ color: rateColour(Number(i.collectionRatePct)), fontWeight: 600 }}>{i.collectionRatePct}%</td>
                  <td>{i.attendanceMarkedDays > 0 ? `${i.attendanceRatePct}%` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' | 'bad' }) {
  const colour = tone === 'good' ? '#1a7f37' : tone === 'bad' ? '#c1272d' : 'inherit';
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="eyebrow" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0 2px', color: colour }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>}
    </div>
  );
}
